import {useCallback, useId, useMemo, useRef, useState} from 'react'

import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'

import type {PlayButtonAction} from '../../../../../brand/components/PlayButton/PlayButton'
import type {VideoPlaybackStateSubscription} from '../../../hooks/use-video'
import {HeroPagination, type HeroPaginationProps} from './HeroPagination/HeroPagination'
import {HeroSequence, type HeroSequenceHandle, type HeroSequenceProps} from './HeroSequence/HeroSequence'

import Styles from './Hero.module.css'

export interface HeroProps {
  a11y: {
    title: string
  }
  heroSequence: Omit<
    HeroSequenceProps,
    'currentChapterIndex' | 'labelledBy' | 'onNextChapter' | 'onReady' | 'ref' | 'video'
  > & {
    video?: Omit<NonNullable<HeroSequenceProps['video']>, 'onActionChange'>
  }
  heroPagination: Omit<HeroPaginationProps, 'onChange' | 'items' | 'currentChapterIndex' | 'video'> & {
    items: string[]
  }
}

export const defaultProps: Partial<HeroProps> = {}

export const Hero = (props: HeroProps) => {
  const initializedProps: HeroProps = {...defaultProps, ...props}
  const {a11y, heroSequence, heroPagination} = initializedProps

  const titleId = useId()
  const prefersReducedMotion = usePrefersReducedMotion()
  const sequenceRef = useRef<HeroSequenceHandle>(null)

  const [currentChapterIndex, setCurrentChapterIndex] = useState(0)
  const [playButtonAction, setPlayButtonAction] = useState<PlayButtonAction>('play')
  const [ready, setReady] = useState<{
    durations: number[]
    playbackStateSubscription?: VideoPlaybackStateSubscription
  } | null>(null)

  const video = useMemo(
    () => (heroSequence.video ? {...heroSequence.video, onActionChange: setPlayButtonAction} : undefined),
    [heroSequence.video],
  )

  const onReady = useCallback<HeroSequenceProps['onReady']>((durations, playbackStateSubscription) => {
    setReady({durations, playbackStateSubscription})
  }, [])

  const selectChapter = useCallback((index: number) => {
    setCurrentChapterIndex(index)
    sequenceRef.current?.seekToChapter(index)
  }, [])

  const playbackStateSubscription = ready?.playbackStateSubscription

  const onTogglePlay = useCallback(() => {
    if (playbackStateSubscription && !prefersReducedMotion) void sequenceRef.current?.setPlayback(playButtonAction)
  }, [playbackStateSubscription, prefersReducedMotion, playButtonAction])

  const paginationVideo = useMemo(
    () => (playbackStateSubscription ? {playButtonAction, playbackStateSubscription, onTogglePlay} : undefined),
    [playbackStateSubscription, onTogglePlay, playButtonAction],
  )

  const items = useMemo(() => {
    // Wait for real video durations; only sequences without video have untimed chapters.
    if (!ready || (heroSequence.video && ready.durations.length === 0)) return []

    return heroPagination.items.map((label, index) => {
      const duration = heroSequence.video ? ready.durations[index] : 0
      if (duration === undefined) throw new Error(`Hero video has no duration for chapter ${index}.`)

      return {label, duration}
    })
  }, [heroPagination.items, heroSequence.video, ready])

  return (
    <div className={Styles['Hero']}>
      <h2 id={titleId} className="visually-hidden">
        {a11y.title}
      </h2>
      <HeroSequence
        {...heroSequence}
        video={video}
        labelledBy={titleId}
        currentChapterIndex={currentChapterIndex}
        onNextChapter={setCurrentChapterIndex}
        onReady={onReady}
        ref={sequenceRef}
      />
      <HeroPagination
        {...heroPagination}
        currentChapterIndex={currentChapterIndex}
        onChange={selectChapter}
        items={items}
        video={heroSequence.video ? paginationVideo : undefined}
      />
    </div>
  )
}
